<template>
  <div class="space-y-2">
    <label :for="id" class="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-dark-300">
      <span>{{ label }}</span>
      <span v-if="required && !modelValue" class="text-xs font-normal text-gray-500 dark:text-gray-400">(Required)</span>
      <i v-else-if="required && modelValue" class="fas fa-check-circle text-success-600 dark:text-success-400 text-xs"></i>
    </label>
    <div class="relative">
      <select
        :id="id"
        :value="modelValue"
        @input="$emit('update:modelValue', $event.target.value)"
        class="mt-1 block w-full px-4 py-2 text-base border rounded-lg focus:ring-2 focus:ring-primary-500 bg-surface-50 dark:bg-surface-700 border-surface-300 dark:border-surface-600 text-surface-900 dark:text-surface-100 sm:text-sm"
        :class="{ 
          'border-error-500 dark:border-error-500 focus:border-error-500 focus:ring-error-500': error || loadError,
          'opacity-50 cursor-not-allowed': loading || disabled
        }"
        :required="required"
        :disabled="loading || disabled"
        @change="handleChange"
      >
        <option value="" disabled class="bg-white dark:bg-dark-700 text-gray-900 dark:text-dark-100">{{ loading ? 'Loading departments...' : 'Select a department' }}</option>
        <option
          v-for="department in departments"
          :key="department.id"
          :value="department.id"
          class="py-2 px-4 hover:bg-blue-50 dark:hover:bg-blue-900/30 bg-white dark:bg-dark-700 text-gray-900 dark:text-dark-100"
        >
          {{ department.name }}
        </option>
      </select>
      <div v-if="loading" class="absolute right-2 top-1/2 transform -translate-y-1/2">
        <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
      </div>
    </div>
    <p v-if="error || loadError" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ error || loadError }}</p>
    <p v-if="description" class="mt-1 text-sm text-gray-500 dark:text-dark-400">{{ description }}</p>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import { useApi } from '@/composables/useApi';
import { useToast } from '@/composables/useToast';
import { useMasterData } from '@/composables/useMasterData';

const props = defineProps({
  modelValue: {
    type: [Number, String],
    default: ''
  },
  label: {
    type: String,
    default: 'Department'
  },
  id: {
    type: String,
    default: 'department'
  },
  required: {
    type: Boolean,
    default: false
  },
  error: {
    type: String,
    default: ''
  },
  description: {
    type: String,
    default: ''
  },
  disabled: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:modelValue', 'change']);

const api = useApi();
const toast = useToast();
const masterData = useMasterData();
const departments = ref([]);
const loading = ref(false);
const loadError = ref('');

const handleChange = (event) => {
  const value = event.target.value;
  emit('change', value);
};

const loadDepartments = async () => {
  try {
    loading.value = true;
    loadError.value = '';
    const result = await masterData.loadDepartments();
    departments.value = result || [];
  } catch (error) {
    console.error('Error loading departments:', error);
    loadError.value = 'Failed to load departments';
    toast.error('Failed to load departments');
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  loadDepartments();
});
</script> 